import { useCallback, useEffect, useRef, useState } from "react"
import DatalakeService from "src/services/datalake-service"
import CalcService from "src/services/calc-service"
import { DATALAKE_CONTRAPARTE_PATH } from "src/utils/api-routes"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"

export const COMPANY_CREATE_REQUIRED_FIELDS = [
    "CPFCNPJ",
    "RazaoSocial",
    "NomeFantasia",
    "Setor",
    "SubSetor",
]

const cleanDocument = (value) => String(value ?? "").replace(/\D/g, "")

const isRepeatedDocument = (value) => /^(\d)\1+$/.test(value)

const isValidCpf = (value) => {
    if (value.length !== 11 || isRepeatedDocument(value)) return false

    const calculateDigit = (base) => {
        const sum = base.split("").reduce(
            (total, digit, index) => total + Number(digit) * (base.length + 1 - index),
            0
        )
        const remainder = (sum * 10) % 11

        return remainder === 10 ? 0 : remainder
    }

    const firstDigit = calculateDigit(value.slice(0, 9))
    const secondDigit = calculateDigit(value.slice(0, 9) + firstDigit)

    return Number(value[9]) === firstDigit && Number(value[10]) === secondDigit
}

const isValidCnpj = (value) => {
    if (value.length !== 14 || isRepeatedDocument(value)) return false

    const calculateDigit = (base) => {
        let weight = base.length - 7
        const sum = base.split("").reduce((total, digit) => {
            const nextTotal = total + Number(digit) * weight
            weight = weight === 2 ? 9 : weight - 1
            return nextTotal
        }, 0)
        const remainder = sum % 11

        return remainder < 2 ? 0 : 11 - remainder
    }

    const firstDigit = calculateDigit(value.slice(0, 12))
    const secondDigit = calculateDigit(value.slice(0, 12) + firstDigit)

    return Number(value[12]) === firstDigit && Number(value[13]) === secondDigit
}

export const isValidCpfCnpj = (value) => {
    const document = cleanDocument(value)

    return isValidCpf(document) || isValidCnpj(document)
}

const normalizePayload = (values) => {
    const document = cleanDocument(values.CPFCNPJ)
    const normalizedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
            key,
            typeof value === "string" ? value.trim() || null : value,
        ])
    )

    return {
        ...normalizedValues,
        CPFCNPJ: document,
        RaizCNPJ: document.slice(0, 8),
    }
}

const hasRequiredFields = (values) => COMPANY_CREATE_REQUIRED_FIELDS.every(
    (field) => String(values[field] ?? "").trim()
)

const getBackendFieldErrors = (error) => {
    const status = error?.response?.status ?? error?.status
    const responseBody = error?.response?.body

    if (status !== 400 || !responseBody || typeof responseBody !== "object") return null

    const fieldErrors = Object.fromEntries(
        Object.entries(responseBody)
            .filter(([, messages]) => Array.isArray(messages) && messages.length > 0)
            .map(([field, messages]) => [field, messages.join(" ")])
    )

    return Object.keys(fieldErrors).length > 0 ? fieldErrors : null
}

export const useCompanyCreate = ({ onCreated, onValidationError }) => {
    const datalakeServiceRef = useRef(new DatalakeService())
    const calcServiceRef = useRef(new CalcService())
    const submittingRef = useRef(false)
    const [submitting, setSubmitting] = useState(false)
    const [periodicities, setPeriodicities] = useState([])
    const [sectors, setSectors] = useState([])

    const loadPeriodicities = useCallback(async () => {
        try {
            const response = await datalakeServiceRef.current.getPeriodicidadeBalanco()
            const values = response?.body?.results ?? []

            setPeriodicities(values.map((item) => ({
                value: item.PeriodicidadeNumero,
                label: item.PeriodicidadeBalanco,
            })))
        } catch (error) {
            setPeriodicities([])
        }
    }, [])

    const loadSectors = useCallback(async () => {
        try {
            const values = await calcServiceRef.current.recuperaSetores()
            setSectors(Array.isArray(values) ? values : [])
        } catch (error) {
            setSectors([])
        }
    }, [])

    const loadSubsectors = useCallback(async (sector) => {
        if (!sector) return []

        try {
            const values = await calcServiceRef.current.recuperaSubsetores(sector)
            return Array.isArray(values) ? values : []
        } catch (error) {
            return []
        }
    }, [])

    useEffect(() => {
        void loadPeriodicities()
        void loadSectors()
    }, [loadPeriodicities, loadSectors])

    const createCompany = useCallback(async (values) => {
        if (submittingRef.current) return false

        if (!isValidCpfCnpj(values?.CPFCNPJ)) {
            dispatchSnackbar("Informe um CPF ou CNPJ válido.", "error")
            return false
        }

        if (!hasRequiredFields(values)) {
            dispatchSnackbar("Preencha os campos obrigatórios para cadastrar a contraparte.", "error")
            return false
        }

        const payload = normalizePayload(values)
        submittingRef.current = true
        setSubmitting(true)

        try {
            const duplicateResponse = await datalakeServiceRef.current.loadPaginatedTable(
                DATALAKE_CONTRAPARTE_PATH,
                { filters: { CPFCNPJ__exact: payload.CPFCNPJ }, limit: 1, offset: 0 }
            )

            if ((duplicateResponse?.body?.count ?? 0) > 0) {
                dispatchSnackbar("Já existe uma contraparte cadastrada com este CPF/CNPJ.", "error")
                return false
            }

            await datalakeServiceRef.current.createTableEndpoint(DATALAKE_CONTRAPARTE_PATH, payload)
            dispatchSnackbar("Contraparte cadastrada com sucesso.", "success")
            await onCreated?.()

            return true
        } catch (error) {
            const fieldErrors = getBackendFieldErrors(error)
            if (fieldErrors) {
                onValidationError?.(fieldErrors)
                return false
            }

            dispatchSnackbar("Não foi possível cadastrar a contraparte. Tente novamente.", "error")
            return false
        } finally {
            submittingRef.current = false
            setSubmitting(false)
        }
    }, [onCreated, onValidationError])

    return {
        submitting,
        periodicities,
        sectors,
        loadPeriodicities,
        loadSubsectors,
        createCompany,
    }
}
